import { breakpointSizes } from 'components/utils/breakpoint'
import { clamp } from 'lib/maths/mathUtils'
import type { MapPadding } from 'lib/maps/types'
import { getWidth } from 'lib/window/getViewport'

interface ResponsivePaddingConfig {
  desktop: MapPadding
  mobile: MapPadding
}

interface ZoomAdjustmentRangeConfig {
  /**
   * At or above this zoom level we keep the base search-bounds padding.
   */
  noAdjustmentAtOrAboveZoom: number

  /**
   * At or below this zoom level the zoomed-out search-bounds padding is fully applied.
   */
  fullAdjustmentAtOrBelowZoom: number

  /**
   * Controls how aggressively the zoomed-out padding stays applied through the
   * transition range. `1` is linear. Values below `1` keep the shrink stronger
   * for longer as the user zooms in from globe mode.
   */
  adjustmentCurveExponent: number

  /**
   * Optional extra boost applied from this zoom upward so the shrink does not
   * fall away too quickly around the mid/high end of the globe range.
   */
  lateStageBoostStartZoom?: number

  /**
   * Maximum multiplier applied at `lateStageBoostStartZoom`. The multiplier
   * linearly fades back to `1` by `noAdjustmentAtOrAboveZoom`.
   */
  lateStageBoostMaxMultiplier?: number
}

interface ZoomAwarePaddingConfig {
  /**
   * Baseline padding used when the user is relatively zoomed in.
   */
  base: MapPadding

  /**
   * Padding used when the map is heavily zoomed out. Tune these values to make
   * the searched area smaller or larger at globe-level zooms.
   */
  zoomedOut: MapPadding
}

interface ResponsiveZoomAwarePaddingConfig {
  desktop: ZoomAwarePaddingConfig
  mobile: ZoomAwarePaddingConfig
}

interface SearchResultsMapPaddingConfig {
  /**
   * Camera fit padding used when we focus the map on markers or destination bounds.
   * Larger values keep more breathing room around pins.
   */
  fitBounds: ResponsivePaddingConfig

  /**
   * One-pixel-smaller variant of `fitBounds` used to avoid the map zooming out one
   * extra level due to rounding when the fitted bounds should exactly match the viewport.
   */
  fitBoundsFudged: ResponsivePaddingConfig

  /**
   * Shared zoom thresholds for search-bounds tuning.
   * The interpolation can be made more aggressive with `adjustmentCurveExponent`.
   */
  searchBoundsZoom: ZoomAdjustmentRangeConfig

  /**
   * Inward crop applied before a map-area search is sent to the URL / API.
   * Larger values make the searched area smaller by trimming more of the viewport edge.
   */
  searchBoundsInset: ResponsiveZoomAwarePaddingConfig

  /**
   * Outward tolerance re-added after the inset step so edge-visible pins do not
   * disappear too early. Larger values make the final search area more forgiving.
   */
  searchBoundsTolerance: ResponsiveZoomAwarePaddingConfig
}

export const searchResultsMapPaddingConfig: SearchResultsMapPaddingConfig = {
  /**
   * Standard fitBounds padding used when positioning the camera around markers or
   * destination bounds. These values are inherited from the previous stable map
   * behaviour, so other teams should treat them as baseline camera spacing rather
   * than search-area tuning knobs.
   */
  fitBounds: {
    /**
     * Desktop gets larger horizontal padding because the map has more side space
     * and we do not want edge markers hugging the viewport border.
     */
    desktop: { top: 67, left: 60, bottom: 40, right: 60 },
    /**
     * Mobile keeps much smaller side padding because the screen is narrow and we
     * need more of the viewport available for actual markers.
     */
    mobile: { top: 70, left: 20, bottom: 48, right: 20 },
  },
  /**
   * One-pixel tighter fitBounds values used only to avoid a rounding issue where
   * the map occasionally zooms out one extra level. These should normally move in
   * lockstep with `fitBounds`; if a team changes one, review the other as well.
   */
  fitBoundsFudged: {
    desktop: { top: 66, left: 59, bottom: 39, right: 59 },
    mobile: { top: 69, left: 19, bottom: 47, right: 19 },
  },
  /**
   * Controls how strongly the special "globe-mode" shrink stays active between
   * zoom 2 and zoom 6. This is the most important group for teams tuning the
   * transition from circular globe bounds back to flatter map behaviour.
   */
  searchBoundsZoom: {
    /**
     * At zoom 6 and above the map behaves close enough to the traditional flat
     * viewport that the old Google-like padding logic is acceptable again.
     *
     * Increase this if the globe-specific shrink still needs to remain active at
     * higher zooms. Decrease it if the special handling should end sooner.
     */
    noAdjustmentAtOrAboveZoom: 6,
    /**
     * At zoom 2 and below the map is fully in globe mode, so the custom shrink
     * must be applied at 100% strength.
     *
     * Increase this if the maximum shrink should kick in earlier. Decrease it if
     * teams want more of the transition to happen below zoom 2.
     */
    fullAdjustmentAtOrBelowZoom: 2,
    /**
     * Non-linear curve applied between zoom 2 and zoom 6.
     *
     * Why `0.65`:
     * Linear interpolation was releasing the shrink too quickly around zoom 4,
     * making the search area grow faster than the user-perceived viewport.
     *
     * Tuning guide:
     * - Lower than `1`: keep shrink stronger for longer.
     * - Closer to `1`: make the transition more linear.
     * - Above `1`: release the shrink faster.
     */
    adjustmentCurveExponent: 0.65,
    /**
     * Start an extra "late-stage" boost at zoom 4 because that is where we
     * observed the shrink becoming visibly too weak, even after applying the
     * global non-linear curve.
     *
     * Increase this if only very high globe zooms need help. Decrease it if the
     * extra boost should begin earlier in the 2~6 range.
     */
    lateStageBoostStartZoom: 4,
    /**
     * Adds up to 30% more shrink at zoom 4, then linearly fades back to `1`
     * by zoom 6 so that the special handling disappears cleanly.
     *
     * Increase this if zoom 4~5 still feels too loose. Decrease it if teams see
     * over-shrinking in the mid/high part of the globe transition.
     */
    lateStageBoostMaxMultiplier: 1.3,
  },
  /**
   * The inward crop applied before we serialise map-area bounds into the search
   * URL / API. These are the primary values to tune when the searched area feels
   * too large or too small relative to what users think they are looking at.
   */
  searchBoundsInset: {
    desktop: {
      /**
       * The exact pre-globe flat-map baseline.
       *
       * Keep this aligned with the long-standing Google Maps behaviour because it
       * is the fallback for:
       * - zoom levels at or above `noAdjustmentAtOrAboveZoom`
       * - call sites that do not pass a zoom value (for example Experiences map)
       *
       * If teams need a tighter 5.5~6 transition, tune the zoom curve or the
       * zoomed-out padding instead of changing this legacy baseline.
       */
      base: { top: 34, left: 0, bottom: 20, right: 0 },
      /**
       * Globe-mode desktop inset.
       *
       * Why these values:
       * - `top: 185`: relaxed slightly after earlier tuning over-cropped the top.
       * - `left/right: 260`: the biggest globe problem was horizontal overreach,
       *   so desktop needs strong side cropping.
       * - `bottom: 210`: kept slightly larger than top to avoid the search area
       *   stretching too far into the lower hemisphere.
       *
       * Tuning guide:
       * - Increase `top/bottom` to shrink the box vertically.
       * - Increase `left/right` to shrink it horizontally.
       * - Decrease any side to let that edge breathe more.
       */
      zoomedOut: { top: 185, left: 260, bottom: 210, right: 260 },
    },
    mobile: {
      /**
       * The exact pre-globe mobile baseline.
       *
       * This must stay in sync with the legacy Google Maps behaviour so non-globe
       * consumers and zoom >= 6 both resolve to the same long-standing bounds.
       */
      base: { top: 35, left: 0, bottom: 24, right: 0 },
      /**
       * Globe-mode mobile inset.
       *
       * These values are intentionally smaller than desktop because mobile already
       * has a much narrower viewport. If mobile teams retune this, keep desktop
       * and mobile separate; they should not be forced to share the same ratios.
       */
      zoomedOut: { top: 96, left: 80, bottom: 110, right: 80 },
    },
  },
  /**
   * The outward compensation applied after `searchBoundsInset`.
   *
   * Why this exists:
   * A strict inset alone can make edge-visible pins disappear earlier than users
   * expect, especially when the map and list are both animating. Tolerance puts
   * back a small amount of breathing room.
   */
  searchBoundsTolerance: {
    desktop: {
      /**
       * Flat-map desktop tolerance from legacy behaviour. This remains relatively
       * generous because the standard map view is stable and familiar.
       */
      base: { top: 12, left: 20, bottom: 12, right: 20 },
      /**
       * Globe-mode desktop tolerance.
       *
       * Why these are small:
       * We already shrink aggressively in globe mode, and large tolerance values
       * would immediately undo that work. Teams should change inset first and only
       * widen tolerance if the final box feels too "snappy" at the edges.
       */
      zoomedOut: { top: 6, left: 8, bottom: 6, right: 8 },
    },
    mobile: {
      /**
       * Mobile baseline tolerance mirrors the existing map behaviour and keeps
       * a small but visible edge buffer for pins.
       */
      base: { top: 8, left: 12, bottom: 8, right: 12 },
      /**
       * Mobile globe tolerance stays very small because the viewport is already
       * constrained; adding too much tolerance on mobile makes the search area
       * grow back out disproportionately quickly.
       */
      zoomedOut: { top: 4, left: 6, bottom: 4, right: 6 },
    },
  },
}

export function getResponsiveSearchResultsMapPadding(config: ResponsivePaddingConfig): MapPadding {
  if (getWidth() >= breakpointSizes.tablet.min) {
    return config.desktop
  }

  return config.mobile
}

function interpolateValue(from: number, to: number, progress: number): number {
  return from + (to - from) * progress
}

function getLateStageBoostMultiplier(
  zoom: number,
  range: ZoomAdjustmentRangeConfig,
): number {
  const { lateStageBoostStartZoom, lateStageBoostMaxMultiplier, noAdjustmentAtOrAboveZoom } = range

  if (
    lateStageBoostStartZoom === undefined ||
    lateStageBoostMaxMultiplier === undefined ||
    lateStageBoostMaxMultiplier <= 1 ||
    zoom < lateStageBoostStartZoom
  ) {
    return 1
  }

  if (zoom >= noAdjustmentAtOrAboveZoom) {
    return 1
  }

  const boostRange = noAdjustmentAtOrAboveZoom - lateStageBoostStartZoom

  if (boostRange <= 0) {
    return 1
  }

  const progressToNoAdjustment = clamp(
    0,
    (zoom - lateStageBoostStartZoom) / boostRange,
    1,
  )

  return interpolateValue(lateStageBoostMaxMultiplier, 1, progressToNoAdjustment)
}

function interpolatePadding(
  from: MapPadding,
  to: MapPadding,
  progress: number,
): MapPadding {
  return {
    top: interpolateValue(from.top, to.top, progress),
    right: interpolateValue(from.right, to.right, progress),
    bottom: interpolateValue(from.bottom, to.bottom, progress),
    left: interpolateValue(from.left, to.left, progress),
  }
}

export function getSearchBoundsZoomAdjustmentProgress(
  zoom: number,
  range: ZoomAdjustmentRangeConfig = searchResultsMapPaddingConfig.searchBoundsZoom,
): number {
  const {
    noAdjustmentAtOrAboveZoom,
    fullAdjustmentAtOrBelowZoom,
    adjustmentCurveExponent,
  } = range

  if (zoom >= noAdjustmentAtOrAboveZoom) {
    return 0
  }

  if (zoom <= fullAdjustmentAtOrBelowZoom) {
    return 1
  }

  const linearProgress = clamp(
    0,
    (noAdjustmentAtOrAboveZoom - zoom) / (noAdjustmentAtOrAboveZoom - fullAdjustmentAtOrBelowZoom),
    1,
  )

  const curvedProgress = Math.pow(linearProgress, adjustmentCurveExponent)

  return clamp(
    0,
    curvedProgress * getLateStageBoostMultiplier(zoom, range),
    1,
  )
}

export function getResponsiveZoomAwareSearchResultsMapPadding(
  config: ResponsiveZoomAwarePaddingConfig,
  zoom?: number,
  range: ZoomAdjustmentRangeConfig = searchResultsMapPaddingConfig.searchBoundsZoom,
): MapPadding {
  const currentConfig = getWidth() >= breakpointSizes.tablet.min ? config.desktop : config.mobile

  if (zoom === undefined) {
    // Preserve legacy flat-map behaviour for call sites that do not participate in
    // the globe-specific zoom interpolation, such as Google Maps consumers.
    return currentConfig.base
  }

  return interpolatePadding(
    currentConfig.base,
    currentConfig.zoomedOut,
    getSearchBoundsZoomAdjustmentProgress(zoom, range),
  )
}

export function getLegacySearchBoundsInsetPadding(): MapPadding {
  return getResponsiveSearchResultsMapPadding({
    desktop: searchResultsMapPaddingConfig.searchBoundsInset.desktop.base,
    mobile: searchResultsMapPaddingConfig.searchBoundsInset.mobile.base,
  })
}

export function getLegacySearchBoundsTolerancePadding(): MapPadding {
  return getResponsiveSearchResultsMapPadding({
    desktop: searchResultsMapPaddingConfig.searchBoundsTolerance.desktop.base,
    mobile: searchResultsMapPaddingConfig.searchBoundsTolerance.mobile.base,
  })
}

export function getZoomAwareSearchBoundsInsetPadding(zoom: number): MapPadding {
  return getResponsiveZoomAwareSearchResultsMapPadding(
    searchResultsMapPaddingConfig.searchBoundsInset,
    zoom,
  )
}

export function getZoomAwareSearchBoundsTolerancePadding(zoom: number): MapPadding {
  return getResponsiveZoomAwareSearchResultsMapPadding(
    searchResultsMapPaddingConfig.searchBoundsTolerance,
    zoom,
  )
}
